import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, User, Lock, Download } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";
import ConfirmDelete from "@/components/ConfirmDelete";

const STATUS = ["prospect", "ongoing", "won", "lost"];
const STATUS_LABEL = { prospect: "Prospek", ongoing: "Berjalan", won: "Menang", lost: "Kalah" };
const money = (v) => "Rp " + (Number(v || 0)).toLocaleString("id-ID");

const EMPTY = {
  name: "", estimated_value: 0, detail: "", costsheet: "",
  activity: "", client_id: "", product_manager_id: "", assigned_user_id: "", status: "prospect",
  start_date: "", end_date: "",
};

export default function Projects() {
  const { user, isReadOnly } = useAuth();
  const isLeader = user?.role === "team_leader";

  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [pms, setPms] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pageItems, ctrl] = usePagination(items, 10);
  const [confirmDel, setConfirmDel] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    const calls = [
      api.get("/projects"),
      api.get("/clients"),
      api.get("/template-activities"),
      api.get("/product-managers"),
    ];
    if (isLeader) calls.push(api.get("/users"));
    const results = await Promise.all(calls);
    setItems(results[0].data);
    setClients(results[1].data);
    setActivities(results[2].data);
    setPms(results[3].data);
    if (isLeader) setUsers(results[4].data);
  };
  useEffect(() => { load(); }, [isLeader]); // eslint-disable-line

  const members = users.filter((u) => u.role === "member");

  const openNew = () => {
    setEdit(null);
    setForm({ ...EMPTY, assigned_user_id: isLeader ? "" : user.id });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEdit(it);
    setForm({
      name: it.name || "",
      estimated_value: it.estimated_value || 0,
      detail: it.detail || "",
      costsheet: it.costsheet || "",
      activity: it.activity || "",
      client_id: it.client_id || "",
      product_manager_id: it.product_manager_id || "",
      assigned_user_id: it.user_id || "",
      status: it.status || "prospect",
      start_date: it.start_date || "",
      end_date: it.end_date || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        estimated_value: Number(form.estimated_value),
        costsheet: (form.costsheet || "").trim(),
        client_id: form.client_id || null,
        product_manager_id: form.product_manager_id || null,
        assigned_user_id: isLeader ? (form.assigned_user_id || null) : null,
      };
      if (edit) await api.patch(`/projects/${edit.id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Tersimpan");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (id) => { await api.delete(`/projects/${id}`); load(); };
  const doConfirmDel = async () => {
    if (!confirmDel) return;
    await remove(confirmDel.id);
    setConfirmDel(null);
  };

  const filtered = items.filter((p) => {
    const q = search.trim().toLowerCase();
    if (q) {
      const clientName = clients.find((c) => c.id === p.client_id)?.name || "";
      const pmName = pms.find((x) => x.id === p.product_manager_id)?.name || "";
      const hay = [
        p.name, p.costsheet, p.status, clientName, pmName,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (dateFrom && p.start_date && p.start_date < dateFrom) return false;
    if (dateTo && p.end_date && p.end_date > dateTo) return false;
    if (dateFrom && !p.start_date) return false;
    return true;
  });
  const [pageFiltered, ctrlF] = usePagination(filtered, 10);

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("Tidak ada data untuk diekspor"); return; }
    const headers = ["Nama", "PIC Presales", "Client", "PIC PM", "Status", "Start", "End", "Estimasi", "Costsheet"];
    const rows = filtered.map((p) => [
      p.name || "",
      ownerLabel(p.user_id) || "",
      clients.find((c) => c.id === p.client_id)?.name || "",
      pmLabel(p.product_manager_id) || "",
      STATUS_LABEL[p.status] || p.status || "",
      p.start_date || "",
      p.end_date || "",
      Number(p.estimated_value || 0),
      p.costsheet || "",
    ]);
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `projects-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`CSV diunduh (${filtered.length} baris)`);
  };
  const activityLabel = (v) => activities.find((a) => a.id === v)?.name || v || "-";
  const pmLabel = (v) => pms.find((p) => p.id === v)?.name || "-";
  const ownerLabel = (uid) => {
    if (!uid) return "-";
    if (uid === user?.id) return `${user.name} (Anda)`;
    const u = users.find((x) => x.id === uid);
    return u ? `${u.name}` : uid;
  };

  // For member edit view we still want to show the assignee info; fetch minimal owner via projects list itself.
  const currentAssigneeLabel = () => {
    if (!edit) return user?.name;
    if (edit.user_id === user?.id) return `${user.name} (Anda)`;
    if (isLeader) return ownerLabel(edit.user_id);
    return "Member Presales";
  };

  return (
    <div className="space-y-8" data-testid="projects-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Project</div>
          <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Pipeline & Estimasi</h1>
          <p className="text-sm text-muted-foreground mt-2">Kelola project presales & nilai estimasi.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportCsv} className="rounded-sm" data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="rounded-sm"
                onClick={openNew}
                disabled={isReadOnly}
                data-testid="new-project-button"
              ><Plus className="h-4 w-4 mr-2" /> Project Baru</Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{edit ? "Edit Project" : "Project Baru"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* PIC Presales (assignee) */}
              <div className="md:col-span-2 rounded-sm border border-border bg-accent/40 p-4" data-testid="project-assignee-block">
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div className="label-mini">PIC Presales (Member yang Menghandle)</div>
                </div>
                {isLeader ? (
                  edit ? (
                    // team leader editing existing — allow reassign
                    <>
                      <Select
                        value={form.assigned_user_id}
                        onValueChange={(v) => setForm({ ...form, assigned_user_id: v })}
                      >
                        <SelectTrigger data-testid="project-assignee-select"><SelectValue placeholder="Pilih member presales" /></SelectTrigger>
                        <SelectContent>
                          {members.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Belum ada member presales.</div>}
                          {members.map((m) => (
                            <SelectItem key={m.id} value={m.id}>{m.name} — {m.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="text-[11px] text-muted-foreground mt-2">
                        Data project ini akan muncul di dashboard member yang dipilih.
                      </div>
                    </>
                  ) : (
                    // team leader creating new — pick a member to assign
                    <>
                      <Select
                        value={form.assigned_user_id}
                        onValueChange={(v) => setForm({ ...form, assigned_user_id: v })}
                      >
                        <SelectTrigger data-testid="project-assignee-select"><SelectValue placeholder="Pilih member presales untuk di-assign" /></SelectTrigger>
                        <SelectContent>
                          {members.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Belum ada member presales.</div>}
                          {members.map((m) => (
                            <SelectItem key={m.id} value={m.id}>{m.name} — {m.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="text-[11px] text-muted-foreground mt-2">
                        Kosongkan untuk menyimpan project atas nama Anda sendiri.
                      </div>
                    </>
                  )
                ) : (
                  // Member: read-only display, always locked to self
                  <div className="flex items-center justify-between gap-3" data-testid="project-assignee-readonly">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                        {(user?.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-semibold">{currentAssigneeLabel()}</div>
                        <div className="text-xs text-muted-foreground mono">{user?.email}</div>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold text-muted-foreground">
                      <Lock className="h-3 w-3" /> Terkunci
                    </div>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <Label className="label-mini">Nama Project</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" />
              </div>
              <div>
                <Label className="label-mini">Nilai Estimasi (Rp)</Label>
                <Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} data-testid="project-value-input" />
              </div>
              <div>
                <Label className="label-mini">Costsheet (No. Dokumen)</Label>
                <Input
                  type="text"
                  placeholder="mis. CS-2026-0142"
                  value={form.costsheet}
                  onChange={(e) => setForm({ ...form, costsheet: e.target.value })}
                  data-testid="project-costsheet-input"
                />
              </div>
              <div>
                <Label className="label-mini">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="(Opsional)" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-mini">PIC Product Manager</Label>
                <Select value={form.product_manager_id} onValueChange={(v) => setForm({ ...form, product_manager_id: v })}>
                  <SelectTrigger data-testid="project-pm-select"><SelectValue placeholder="(Opsional) Pilih PM" /></SelectTrigger>
                  <SelectContent>
                    {pms.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Belum ada PM. Tambahkan di menu Product Manager.</div>}
                    {pms.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{p.product ? ` · ${p.product}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-mini">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-mini">Tanggal Mulai</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="project-start-date-input" />
              </div>
              <div>
                <Label className="label-mini">Tanggal Selesai</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="project-end-date-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="label-mini">Activity</Label>
                <Select value={form.activity} onValueChange={(v) => setForm({ ...form, activity: v })}>
                  <SelectTrigger data-testid="project-activity-select">
                    <SelectValue placeholder="Pilih dari template activity" />
                  </SelectTrigger>
                  <SelectContent>
                    {activities.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Belum ada template. Tambah di menu Template Activity.</div>}
                    {activities.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="label-mini">Detail</Label>
                <Textarea value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} data-testid="project-detail-input" />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" className="w-full rounded-sm" data-testid="save-project-button">Simpan</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="card-tech p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2">
          <Label className="label-mini">Cari</Label>
          <Input
            placeholder="Nama project, client, PM, no costsheet, status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="project-search-input"
          />
        </div>
        <div>
          <Label className="label-mini">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="project-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              {STATUS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mini">Mulai ≥</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="project-date-from" />
        </div>
        <div>
          <Label className="label-mini">Selesai ≤</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="project-date-to" />
        </div>
      </div>

      <div className="card-tech p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-6 py-3">Nama</th>
                {isLeader && <th className="text-left px-6 py-3">PIC Presales</th>}
                <th className="text-left px-6 py-3">Client</th>
                <th className="text-left px-6 py-3">PIC PM</th>
                <th className="text-left px-6 py-3">Periode</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-right px-6 py-3">Estimasi</th>
                <th className="text-left px-6 py-3">Costsheet</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={isLeader ? 9 : 8} className="px-6 py-10 text-center text-muted-foreground">Belum ada project yang cocok.</td></tr>}
              {pageFiltered.map((it) => (
                <tr key={it.id} className="border-t border-border hover:bg-accent/50" data-testid={`project-row-${it.id}`}>
                  <td className="px-6 py-3 font-medium">{it.name}</td>
                  {isLeader && <td className="px-6 py-3 text-muted-foreground">{ownerLabel(it.user_id)}</td>}
                  <td className="px-6 py-3 text-muted-foreground">{clients.find((c) => c.id === it.client_id)?.name || "-"}</td>
                  <td className="px-6 py-3 text-muted-foreground">{pmLabel(it.product_manager_id)}</td>
                  <td className="px-6 py-3 mono text-xs">
                    {it.start_date || "-"}<span className="mx-1 text-muted-foreground">→</span>{it.end_date || "-"}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${
                      it.status === "won" ? "bg-emerald-100 text-emerald-800" :
                      it.status === "lost" ? "bg-red-100 text-red-800" :
                      it.status === "ongoing" ? "bg-blue-100 text-blue-800" :
                      "bg-slate-200 text-slate-800"
                    }`}>{STATUS_LABEL[it.status] || it.status}</span>
                  </td>
                  <td className="px-6 py-3 text-right mono">{money(it.estimated_value)}</td>
                  <td className="px-6 py-3 mono text-xs">{it.costsheet || "-"}</td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    {!isReadOnly && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-project-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDel(it)} data-testid={`delete-project-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination ctrl={ctrlF} testidPrefix="projects-pg" />
      </div>
      <ConfirmDelete
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doConfirmDel}
        entityLabel={confirmDel?.name}
        testIdPrefix="confirm-delete-project"
      />
    </div>
  );
}
